import React, { useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { erpAPI } from '../../services/api';
import SearchableSelect from './SearchableSelect';
import {
  LayoutDashboard, FolderOpen, Package, ShoppingBag, ClipboardList,
  Users, Star, Ticket, Image, Truck, Home, FileText, Phone,
  HelpCircle, RotateCcw, ScrollText, Lock, Palette, Plug2,
  MapPin, Radio, TrendingUp, Link as LinkIcon, Tag, Globe, User,
  ChevronLeft, ChevronRight, ChevronDown, RefreshCw, ExternalLink, LogOut, Menu,
  Moon, Sun, Plus,
  Warehouse, Boxes, ArrowRightLeft, ShoppingCart, BookOpen, Settings2,
  CreditCard, History, DollarSign, ArrowUpFromLine, Wallet,
} from 'lucide-react';
import './AdminLayout.css';

interface MenuItem {
  path: string;
  icon: React.ReactNode;
  label: string;
}

interface MenuGroup {
  icon: React.ReactNode;
  label: string;
  children: SidebarItem[];
}

type SidebarItem = MenuItem | MenuGroup;

function isMenuGroup(item: SidebarItem): item is MenuGroup {
  return 'children' in item;
}

function normalizePath(path: string) {
  return path.split('?')[0];
}

function flattenMenuPaths(items: SidebarItem[]): string[] {
  return items.flatMap((item) => isMenuGroup(item) ? flattenMenuPaths(item.children) : [normalizePath(item.path)]);
}

const AdminLayout: React.FC = () => {
  const { i18n } = useTranslation();
  const { logout, user, isERPUser, erpIdentity } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const [topLocations,setTopLocations]=useState<any[]>([]);
  const [topLocation,setTopLocation]=useState(()=>sessionStorage.getItem('erp-location-filter')||'ALL');
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('admin-dark-mode');
    return saved ? JSON.parse(saved) : false;
  });

  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  React.useEffect(()=>{if(!isERPUser)return;erpAPI.list('locations').then(response=>{const rows=Array.isArray(response.data)?response.data:(response.data.results||[]);setTopLocations(rows);if(rows.length===1){const id=String(rows[0].id);setTopLocation(id);sessionStorage.setItem('erp-location-filter',id);window.dispatchEvent(new CustomEvent('erp-location-change',{detail:{location:id}}))}}).catch(()=>setTopLocations([]))},[isERPUser]);
  const changeTopLocation=(next:string)=>{const locationId=next==='ALL'?'':next;setTopLocation(next);sessionStorage.setItem('erp-location-filter',locationId);window.dispatchEvent(new CustomEvent('erp-location-change',{detail:{location:locationId}}))};
  React.useEffect(()=>{const handler=(e:Event)=>{const{location}=((e as CustomEvent).detail||{});if(location!==undefined){setTopLocation(location||'ALL');}};window.addEventListener('erp-location-change',handler);return()=>window.removeEventListener('erp-location-change',handler)},[]);

  React.useEffect(() => {
    localStorage.setItem('admin-dark-mode', JSON.stringify(darkMode));
    
    // Disable transitions temporarily for smooth theme switch
    document.documentElement.classList.add('theme-transitioning');
    
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Re-enable transitions after a brief delay
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transitioning');
    }, 50);
  }, [darkMode]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const fullAdmin = Boolean(user?.is_superuser || erpIdentity?.is_admin);
  const permissions = new Set<string>(erpIdentity?.permissions || []);
  const can = (code:string) => fullAdmin || permissions.has(code);
  const operationalCatalog = can('product.view') || can('inventory.view');
  const accountChildren: SidebarItem[] = [
    ...(can('accounts.overview.view') ? [{ path: '/admin/accounts/overview', icon: <LayoutDashboard size={16} />, label: 'Dashboard' }] : []),
    ...(can('accounts.cash_bank.view') ? [{ path: '/admin/accounts/cash-bank', icon: <CreditCard size={16} />, label: 'Cash & Banks' }] : []),
    ...(can('accounts.receivable.view') ? [{ path: '/admin/accounts/receivables', icon: <ShoppingBag size={16} />, label: 'Receivables' }] : []),
    ...(can('accounts.payable.view') ? [{ path: '/admin/accounts/payables', icon: <ClipboardList size={16} />, label: 'Payables' }] : []),
    ...(can('accounts.view') ? [{ path: '/admin/accounts/expenses', icon: <ArrowUpFromLine size={16} />, label: 'Expenses' }] : []),
    ...(can('accounts.view') ? [{ path: '/admin/accounts/journal-entries', icon: <BookOpen size={16} />, label: 'Journal Entries' }] : []),
    ...(can('accounts.transaction.view') ? [{ path: '/admin/accounts/transactions', icon: <ArrowRightLeft size={16} />, label: 'Transactions' }] : []),
    ...(can('accounts.ledger.view') ? [{ path: '/admin/accounts/ledger', icon: <ScrollText size={16} />, label: 'General Ledger' }] : []),
    ...(can('accounts.profit_loss.view') ? [{ path: '/admin/accounts/financial-reports', icon: <TrendingUp size={16} />, label: 'Financial Reports' }] : []),
    ...(can('accounts.view') ? [{ path: '/admin/accounts/chart', icon: <Wallet size={16} />, label: 'Chart of Accounts' }] : []),
    ...(can('accounts.view') ? [{ path: '/admin/accounts/settings', icon: <Settings2 size={16} />, label: 'Settings' }] : []),
  ];

  const inventoryChildren: SidebarItem[] = [
    ...(can('inventory.view') ? [{ path: '/admin/inventory', icon: <Warehouse size={16} />, label: 'Dashboard' }] : []),
    ...(can('transfer.view') ? [{ path: '/admin/inventory/transfers', icon: <ArrowRightLeft size={16} />, label: 'Stock Transfers' }] : []),
    ...(can('inventory.view') ? [{ path: '/admin/inventory/movements', icon: <History size={16} />, label: 'Stock Movements' }] : []),
  ];

  const accessSetupChildren: SidebarItem[] = [
    ...(isERPUser && (fullAdmin || can('inventory.view') || can('accounts.view')) ? [{ path: '/admin/business-locations', icon: <MapPin size={16} />, label: 'Business Locations' }] : []),
    ...(isERPUser && can('user.view') ? [{ path: '/admin/users-access', icon: <Users size={16} />, label: 'Users & Access' }] : []),
    ...(isERPUser && can('settings.role_manage') ? [{ path: '/admin/manage-roles', icon: <Lock size={16} />, label: 'Manage Roles' }] : []),
  ];
  const storeSetupChildren: SidebarItem[] = fullAdmin ? [
      { path: '/admin/coupons', icon: <Ticket size={16} />, label: 'Coupons & Discounts' },
      { path: '/admin/delivery-options', icon: <Truck size={16} />, label: 'Delivery Options' },
      { path: '/admin/settings', icon: <Palette size={16} />, label: 'Web Config' },
      { path: '/admin/integrations', icon: <Plug2 size={16} />, label: 'Integrations' },
      { path: '/admin/gtm', icon: <Tag size={16} />, label: 'Google Tag Manager' },
      { path: '/admin/translations', icon: <Globe size={16} />, label: 'Translations' },
      { path: '/admin/banners', icon: <Image size={16} />, label: 'Banners' },
  ] : [];
  const cmsSetupChildren: SidebarItem[] = fullAdmin ? [
      { path: '/admin/cms/home', icon: <Home size={16} />, label: 'CMS Home' },
      { path: '/admin/cms/about', icon: <FileText size={16} />, label: 'CMS About' },
      { path: '/admin/cms/contact', icon: <Phone size={16} />, label: 'CMS Contact' },
      { path: '/admin/cms/faq', icon: <HelpCircle size={16} />, label: 'CMS FAQ' },
      { path: '/admin/cms/shipping', icon: <Truck size={16} />, label: 'CMS Shipping Policy' },
      { path: '/admin/cms/returns', icon: <RotateCcw size={16} />, label: 'CMS Returns' },
      { path: '/admin/cms/terms', icon: <ScrollText size={16} />, label: 'CMS Terms' },
      { path: '/admin/cms/privacy', icon: <Lock size={16} />, label: 'CMS Privacy' },
  ] : [];
  const metaSetupChildren: SidebarItem[] = fullAdmin ? [
      { path: '/admin/pixel', icon: <MapPin size={16} />, label: 'Facebook Pixel' },
      { path: '/admin/capi', icon: <Radio size={16} />, label: 'Facebook CAPI' },
      { path: '/admin/meta-ads/config', icon: <Radio size={16} />, label: 'Meta Ads Config' },
      { path: '/admin/meta-ads/report', icon: <TrendingUp size={16} />, label: 'Meta Ads Report' },
      { path: '/admin/meta-ads/products', icon: <LinkIcon size={16} />, label: 'Campaign Products' },
  ] : [];
  const tiktokSetupChildren: SidebarItem[] = fullAdmin ? [
      { path: '/admin/tiktok-pixel', icon: <MapPin size={16} />, label: 'TikTok Pixel' },
      { path: '/admin/tiktok-events-api', icon: <Radio size={16} />, label: 'TikTok Events API' },
  ] : [];
  const settingsChildren: SidebarItem[] = [
    ...(accessSetupChildren.length ? [{ icon: <Lock size={16} />, label: 'Access & Roles', children: accessSetupChildren }] as SidebarItem[] : []),
    ...(storeSetupChildren.length ? [{ icon: <Settings2 size={16} />, label: 'Store Settings', children: storeSetupChildren }] as SidebarItem[] : []),
    ...(cmsSetupChildren.length ? [{ icon: <FileText size={16} />, label: 'CMS', children: cmsSetupChildren }] as SidebarItem[] : []),
    ...(metaSetupChildren.length ? [{ icon: <TrendingUp size={16} />, label: 'Meta Ads', children: metaSetupChildren }] as SidebarItem[] : []),
    ...(tiktokSetupChildren.length ? [{ icon: <Radio size={16} />, label: 'TikTok', children: tiktokSetupChildren }] as SidebarItem[] : []),
  ];
  const sidebarItems: SidebarItem[] = [
    ...(fullAdmin ? [{ path: '/admin/dashboard', icon: <LayoutDashboard size={18} />, label: 'Dashboard' }] as SidebarItem[] : []),
    ...(isERPUser ? [{ path: '/admin/operations', icon: <Warehouse size={18} />, label: 'My ERP Workspace' }] as SidebarItem[] : []),
    ...((fullAdmin || operationalCatalog) ? [{ icon: <Package size={18} />, label: 'Products', children: [
      { path: '/admin/categories', icon: <FolderOpen size={16} />, label: 'Categories' },
      { path: '/admin/catalog/products/new', icon: <Plus size={16} />, label: 'Add Product' },
      { path: '/admin/products', icon: <Package size={16} />, label: 'Product List' },
      { path: '/admin/variant-settings', icon: <Settings2 size={16} />, label: 'Manage Variants' },
    ] }] as SidebarItem[] : []),
    ...(isERPUser && can('purchase.view') ? [
      { icon: <ClipboardList size={18} />, label: 'Purchases', children: [
        { path: '/admin/purchases/add', icon: <Plus size={16} />, label: 'Add Purchase' },
        { path: '/admin/purchases/history', icon: <ScrollText size={16} />, label: 'Purchase History' },
        { path: '/admin/purchases/suppliers', icon: <Users size={16} />, label: 'Manage Suppliers' },
      ] },
    ] as SidebarItem[] : []),
    ...(isERPUser && (can('inventory.view') || can('transfer.view')) ? [{
      icon: <Boxes size={18} />, label: 'Inventory', children: [
        ...(can('inventory.view') ? [{ path: '/admin/inventory/stock-summary', icon: <Boxes size={16} />, label: 'Stock Summary' }] : []),
        ...(can('transfer.view') ? [
          { path: '/admin/inventory/transfers', icon: <ArrowRightLeft size={16} />, label: 'Manage Stocks' },
        ] : []),
        ...(can('inventory.view') ? [
          { path: '/admin/inventory/stock-movements', icon: <History size={16} />, label: 'Movement History' },
        ] : []),
        ...(can('inventory.view') ? [
          { path: '/admin/inventory/stock-adjustment', icon: <ClipboardList size={16} />, label: 'Stock Adjustment' },
        ] : []),
        ...(erpIdentity?.is_admin || erpIdentity?.is_superuser ? [
          { path: '/admin/inventory/settings', icon: <Settings2 size={16} />, label: 'Inventory Settings' },
        ] : []),
      ],
    }] as SidebarItem[] : []),
    ...(isERPUser && can('accounts.view') && accountChildren.length ? [
      { icon: <BookOpen size={18} />, label: 'Accounts', children: accountChildren },
    ] as SidebarItem[] : []),
    ...(isERPUser && (can('pos.sale.view') || can('pos.sale.create') || can('website_order.view')) ? [{
      icon: <ShoppingCart size={18}/>, label: 'Sales', children: [
        ...(can('pos.sale.create') ? [{path:'/admin/sale-order',icon:<ShoppingCart size={16}/>,label:'Sale Order'}] : []),
        ...(can('pos.sale.view') ? [{path:'/admin/sale-orders',icon:<ShoppingBag size={16}/>,label:'Sale Order List'}] : []),
        ...(can('pos.exchange.view') ? [{path:'/admin/pos-exchanges',icon:<RotateCcw size={16}/>,label:'Return & Exchange'}] : []),
        ...(can('website_order.view') ? [{path:'/admin/orders',icon:<ClipboardList size={16}/>,label:'Website Orders'}] : []),
      ]
    }] as SidebarItem[] : []),
    ...(fullAdmin ? [
    { path: '/admin/fulfillment',     icon: <ClipboardList size={18} />,   label: 'Shipping Center' },
    { path: '/admin/customers',       icon: <Users size={18} />,           label: 'Customers' },
    { path: '/admin/reviews',         icon: <Star size={18} />,            label: 'Reviews' },
    ] as SidebarItem[] : []),
    ...(settingsChildren.length ? [{ icon: <Settings2 size={18} />, label: 'Settings', children: settingsChildren }] as SidebarItem[] : []),
    { path: '/admin/profile',      icon: <User size={18} />,   label: 'My Profile' },
  ];
  const allowedPathKey = flattenMenuPaths(sidebarItems).join('|');

  React.useEffect(() => {
    if (fullAdmin || !erpIdentity) return;
    const allowed = allowedPathKey ? allowedPathKey.split('|') : [];
    if (!allowed.some(path => location.pathname === path || location.pathname.startsWith(`${path}/`))) {
      navigate('/admin/operations', { replace: true });
    }
  }, [location.pathname, erpIdentity, fullAdmin, navigate, allowedPathKey]);

  function isPathActive(path: string) {
    const normalized = normalizePath(path);
    return location.pathname === normalized || location.pathname.startsWith(`${normalized}/`);
  }

  const isGroupActive = (group: MenuGroup): boolean =>
    group.children.some((child) => isMenuGroup(child) ? isGroupActive(child) : isPathActive(child.path));

  const isGroupOpen = (key: string, group: MenuGroup) =>
    openMenus[key] !== undefined ? openMenus[key] : isGroupActive(group);

  const toggleGroup = (key: string) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const findActiveTrail = (items: SidebarItem[], trail: string[] = []): string[] | null => {
    for (const item of items) {
      if (isMenuGroup(item)) {
        const match = findActiveTrail(item.children, [...trail, item.label]);
        if (match) return match;
      } else if (isPathActive(item.path)) {
        return [...trail, item.label];
      }
    }
    return null;
  };

  const getPageTitle = (): string => {
    return findActiveTrail(sidebarItems)?.join(' › ') || 'Admin';
  };

  const renderSidebarItem = (item: SidebarItem, depth = 0, parentKey = 'root'): React.ReactNode => {
    if (isMenuGroup(item)) {
      const key = `${parentKey}/${item.label}`;
      const active = isGroupActive(item);
      const open = isGroupOpen(key, item);
      return (
        <li key={key} className={`nav-group ${depth > 0 ? 'nav-group-nested' : ''}`}>
          <button
            type="button"
            className={`nav-item nav-group-header ${depth > 0 ? 'nav-subitem nav-nested-group-header' : ''} depth-${depth} ${active ? 'active' : ''}`}
            onClick={() => toggleGroup(key)}
          >
            <span className="nav-icon">{item.icon}</span>
            {sidebarOpen && <span className="nav-label">{item.label}</span>}
            {sidebarOpen && (
              <span className={`nav-group-chevron ${open ? 'open' : ''}`}>
                <ChevronDown size={15} />
              </span>
            )}
          </button>
          {sidebarOpen && open && (
            <ul className={`nav-submenu depth-${depth + 1}`}>
              {item.children.map((child) => renderSidebarItem(child, depth + 1, key))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li key={`${parentKey}/${item.path}`}>
        <Link
          to={item.path}
          className={`nav-item ${depth > 0 ? 'nav-subitem' : ''} depth-${depth} ${isPathActive(item.path) ? 'active' : ''}`}
        >
          <span className="nav-icon">{item.icon}</span>
          {sidebarOpen && <span className="nav-label">{item.label}</span>}
        </Link>
      </li>
    );
  };

  return (
    <div className={`admin-layout ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'} ${mobileOpen ? 'mobile-nav-open' : ''}`}>
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'closed'} ${mobileOpen ? 'mobile-visible' : ''}`}>
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            E-Store Admin
          </Link>
          <button
            className="toggle-sidebar-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul>
            {sidebarItems.map((item) => renderSidebarItem(item))}
          </ul>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <header className="admin-header">
          <div className="header-left">
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation"
            >
              <Menu size={22} />
            </button>
            <h2 className="page-title">
              {getPageTitle()}
            </h2>
          </div>
          <div className="header-right">
             {isERPUser&&topLocations.length>1&&<div className="header-location-select"><SearchableSelect value={topLocation} onChange={changeTopLocation} placeholder="All permitted locations" options={[{value:'ALL',label:'All permitted locations'},...topLocations.map(item=>({value:item.id,label:`${item.name} (${item.location_type})`}))]}/></div>}
             <button
               className="refresh-btn"
               onClick={() => window.location.reload()}
               title="Refresh page data"
               style={{
                 padding: '0.4rem 0.75rem',
                 borderRadius: '6px',
                 border: '1px solid #e5e7eb',
                 background: 'white',
                 color: '#6b7280',
                 fontSize: '0.8rem',
                 cursor: 'pointer',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '4px',
                 transition: 'all 0.2s',
               }}
               onMouseEnter={(e) => {
                 e.currentTarget.style.borderColor = '#0284c7';
                 e.currentTarget.style.color = '#0284c7';
               }}
               onMouseLeave={(e) => {
                 e.currentTarget.style.borderColor = '#e5e7eb';
                 e.currentTarget.style.color = '#6b7280';
               }}
             >
               <RefreshCw size={14} />
             </button>
             <button
               className="dark-mode-toggle"
               onClick={() => setDarkMode(!darkMode)}
               title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
               style={{
                 padding: '0.4rem 0.75rem',
                 borderRadius: '6px',
                 border: '1px solid #e5e7eb',
                 background: darkMode ? '#374151' : 'white',
                 color: darkMode ? '#fbbf24' : '#6b7280',
                 fontSize: '0.8rem',
                 cursor: 'pointer',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '4px',
                 transition: 'all 0.2s',
               }}
               onMouseEnter={(e) => {
                 e.currentTarget.style.borderColor = darkMode ? '#fbbf24' : '#0284c7';
                 e.currentTarget.style.color = darkMode ? '#fbbf24' : '#0284c7';
               }}
               onMouseLeave={(e) => {
                 e.currentTarget.style.borderColor = darkMode ? '#4b5563' : '#e5e7eb';
                 e.currentTarget.style.color = darkMode ? '#fbbf24' : '#6b7280';
               }}
             >
               {darkMode ? <Sun size={14} /> : <Moon size={14} />}
             </button>
             <button
               className="lang-toggle"
               onClick={() => i18n.changeLanguage(i18n.language === 'bn' ? 'en' : 'bn')}
             >
               {i18n.language === 'bn' ? 'EN' : 'BN'}
             </button>
            <a href="/" target="_blank" rel="noopener noreferrer" className="view-site-btn header-hide-sm" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              View Site <ExternalLink size={14} />
            </a>
            <div className="header-user-info header-hide-sm">
              <span>Welcome, {user?.first_name || user?.username || 'Admin'}</span>
            </div>
            <button onClick={handleLogout} className="header-logout-btn" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span className="header-hide-sm">Logout</span>
              <LogOut size={16} />
            </button>
          </div>
        </header>

        <div className="admin-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
